import React, { useContext } from "react";
import { LoggerContext } from "context/LoggerContext";
import styles from "./ContainerSection.module.scss";
import SectionWrapper from "components/elements/SectionWrapper";
import Section from "components/Section";

/**
 * Container Section that can hold other sections as subsections
 * Using Bootstrap grid system for layout and respecting API-provided column widths
 *
 * @param {Object} props - Section properties
 * @param {Array} props.subsections - Array of subsection configurations
 * @param {string} props.containerLayout - Layout style for the container ("grid", "flow", "columns")
 * @param {number} props.col_width - Column width for the container (1-12)
 * @param {boolean} props.hideSubsections - Whether to hide subsections
 */
const ContainerSection = (props) => {
	const { logDebug, logError } = useContext(LoggerContext);
	
	// Explicit destructuring to highlight all available props and ensure they're properly accessed
	const { 
		subsections = [], 
		hideSubsections = false, 
		containerLayout = "flow",   // Default to 'flow' if not specified
		col_width = 12,
		col_width_content = 12, 
		items_per_line = 3, 
		organization, 
		children,
        isInContainerGrid,
		...otherProps  // Capture remaining props for debugging
	} = props;

	// Enhanced debugging to check containerLayout value coming in
	logDebug("ContainerSection props received:", {
		containerLayout,
		propContainerLayout: props.containerLayout, // Direct access to check if properly passed
		propsKeys: Object.keys(props),  // Check what keys are actually present
		subsectionsCount: subsections.length,
		firstSubsection: subsections[0]?.title,
		otherPropsKeys: Object.keys(otherProps)
	});

    logDebug("isInContainerGridisInContainerGrid", isInContainerGrid);

	// Force-validate the containerLayout prop to catch type issues
	let validatedLayout = containerLayout;
	if (typeof validatedLayout !== 'string' || !['flow', 'grid', 'columns'].includes(validatedLayout)) {
		logError(`Invalid containerLayout value: ${JSON.stringify(validatedLayout)}, falling back to "flow"`);
		validatedLayout = "flow";
	}

	return (
		<SectionWrapper
			className={styles.ContainerSection}
			containerLayout={validatedLayout} // Pass to SectionWrapper but it won't add it as DOM attribute
			{...props}>
			
			{!hideSubsections && Array.isArray(subsections) && subsections.length > 0 && (
				<div 
					className="container-fluid p-0"
					data-container-layout={validatedLayout}>  {/* For visual inspection in browser */}
					
					{validatedLayout === "flow" ? (
						// Flow layout: stacked sections with API-defined widths
						<div className="row justify-content-center">
							<div className={`col-${col_width_content || col_width || 12}`}>
								{subsections.map((subsection, index) => {
                                    const modifiedSubsection = {
                                        ...subsection,
                                        isInContainerGrid: false,
                                        isSubsection: true
                                    };
                                    return (
                                        <div 
                                            key={`subsection-wrapper-${index}`} 
                                            className="mb-4">
                                            <Section
                                                key={`subsection-${subsection.id || index}`}
                                                section={modifiedSubsection}
                                                isFirst={false}
                                                organization={organization}
                                            />
                                        </div>
                                    );
								})}
							</div>
						</div>
					) : validatedLayout === "grid" ? (
						// Grid layout: sections in a grid based on the items_per_line
						<div className="row g-5">
							{subsections.map((subsection, index) => {
								// Calculate column width based on items_per_line or subsection.col_width
								const effectiveColWidth = subsection.col_width || Math.floor(12 / Math.min(items_per_line, 6));
								
								// CRITICAL FIX: Mark subsection as being part of a grid to prevent double width calculations
								const modifiedSubsection = {
									...subsection,
									isInContainerGrid: true, // This flag tells SectionWrapper to not restrict width again
									parentGridColWidth: effectiveColWidth, // Pass the parent column width information
									gridLayoutContext: true, // Additional flag for SectionWrapper context
                                    isSubsection: true
								};
								
								return (
									<div 
										key={`subsection-wrapper-${index}`} 
										className={`col-lg-${effectiveColWidth}`}
										data-col-width={effectiveColWidth}
										data-index={index}>
										<Section
											key={`subsection-${subsection.id || index}`}
											section={modifiedSubsection}
											isFirst={false}
											organization={organization}
										/>
									</div>
								);
							})}
						</div>
					) : (
						// Columns layout: sections with equal width columns
						<div className="row">
							{subsections.map((subsection, index) => {
								// For columns layout: Use explicit col_width or divide space equally
								const colWidth = 12 / Math.min(subsections.length, 4);
								// Respect explicit column width if defined
								const effectiveColWidth = subsection.col_width || Math.floor(colWidth);
								
								// CRITICAL FIX: Also mark column layout subsections accordingly
								const modifiedSubsection = {
									...subsection,
									isInContainerGrid: true,
									parentGridColWidth: effectiveColWidth,
									gridLayoutContext: true
								};
								
								return (
									<div 
										key={`subsection-wrapper-${index}`} 
										className={`col-${effectiveColWidth} mb-4`}
										data-col-width={effectiveColWidth}
										data-index={index}>
										<Section
											key={`subsection-${subsection.id || index}`}
											section={modifiedSubsection}
											isFirst={false}
											organization={organization}
										/>
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
			
			{/* Render children if any */}
			{children}
		</SectionWrapper>
	);
};

export default ContainerSection;
